import React, { useState, useCallback } from 'react';
import { Clapperboard, Play, BookOpen, Clock, BarChart3 } from 'lucide-react';
import { AiFillTikTok } from 'react-icons/ai';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import VideoModal, { DifficultyBadge } from '../components/VideoModal';
import { GUIDES, DIFFICULTY, GUIDES_THUMBNAIL_FALLBACK, getFeaturedGuide } from '../data/guides';

const DISCORD_URL = 'https://discord.com/invite/RwpMfRdzex';

function PlayButton({ size = 'md' }) {
  const cls = size === 'xl' ? 'play-btn play-btn--xl' : 'play-btn';
  const iconSize = size === 'xl' ? 38 : 22;
  return (
    <span className={cls} aria-hidden="true">
      <Play size={iconSize} fill="currentColor" />
    </span>
  );
}

function GuideThumb({ src, alt }) {
  const [errored, setErrored] = useState(false);
  const finalSrc = errored ? GUIDES_THUMBNAIL_FALLBACK : src;
  return (
    <img
      src={finalSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
    />
  );
}

function ProviderMark({ guide }) {
  if (guide.provider !== 'tiktok') return null;
  return (
    <span className="guide-provider-mark" aria-hidden="true">
      <AiFillTikTok size={15} />
    </span>
  );
}

function FeaturedGuideCard({ guide, onPlay }) {
  if (!guide) return null;
  return (
    <article
      className={`guides-featured guides-featured--${guide.format} anim-fade-up`}
      style={{ animationDelay: '0.25s' }}
      data-testid="guide-featured"
    >
      <button
        type="button"
        className="guides-featured-thumb"
        onClick={() => onPlay(guide)}
        aria-label={`Reproducir ${guide.title}`}
      >
        <GuideThumb src={guide.thumbnail} alt={guide.title} />
        <span className="guides-featured-overlay" aria-hidden="true" />
        <ProviderMark guide={guide} />
        <PlayButton size="xl" />
      </button>

      <div className="guides-featured-info">
        <div className="guides-featured-eyebrow">
          <Clapperboard size={15} />
          <span>VÍDEO DESTACADO</span>
        </div>
        <h2 className="guides-featured-title">{guide.title}</h2>
        <p className="guides-featured-desc">{guide.description}</p>
        <div className="guides-featured-meta">
          {guide.duration && <span className="meta-chip"><Clock size={13} /> {guide.duration}</span>}
          <span className="meta-chip"><BarChart3 size={13} /> {DIFFICULTY[guide.difficulty]?.label}</span>
        </div>
        <button
          type="button"
          onClick={() => onPlay(guide)}
          className="guides-featured-cta"
          data-testid="guide-featured-cta"
        >
          VER GUÍA COMPLETA
        </button>
      </div>
    </article>
  );
}

function ShortCard({ guide, onPlay }) {
  const soon = !guide.videoId;
  return (
    <article className="guide-short" data-testid={`guide-short-${guide.id}`}>
      <button
        type="button"
        className="guide-short-thumb"
        onClick={() => onPlay(guide)}
        aria-label={soon ? `${guide.title} (próximamente)` : `Reproducir ${guide.title}`}
      >
        <GuideThumb src={guide.thumbnail} alt="" />
        <ProviderMark guide={guide} />
        {soon ? (
          <span className="guide-short-tag">PRÓXIMAMENTE</span>
        ) : (
          <>
            <PlayButton size="md" />
            {guide.duration && <span className="guide-short-duration">{guide.duration}</span>}
          </>
        )}
      </button>
      <div className="guide-short-body">
        <h3 className="guide-short-title">{guide.title}</h3>
        <div className="guide-short-meta">
          {guide.duration && <span className="meta-chip"><Clock size={12} /> {guide.duration}</span>}
          <DifficultyBadge level={guide.difficulty} />
        </div>
      </div>
    </article>
  );
}

function GuideCard({ guide, onPlay }) {
  return (
    <article className="guide-card" data-testid={`guide-card-${guide.id}`}>
      <button
        type="button"
        className="guide-card-thumb"
        onClick={() => onPlay(guide)}
        aria-label={`Reproducir ${guide.title}`}
      >
        <GuideThumb src={guide.thumbnail} alt={guide.title} />
        <PlayButton size="md" />
      </button>

      <div className="guide-card-number" aria-hidden="true">{guide.number}</div>

      <div className="guide-card-body">
        <h3 className="guide-card-title">{guide.title}</h3>
        <p className="guide-card-desc">{guide.description}</p>
      </div>

      <div className="guide-card-meta">
        {guide.duration && <span className="meta-chip"><Clock size={12} /> {guide.duration}</span>}
      </div>

      <div className="guide-card-difficulty">
        <DifficultyBadge level={guide.difficulty} />
      </div>
    </article>
  );
}

const featured = getFeaturedGuide();
const shorts = GUIDES.filter((g) => g.format === 'vertical' && g.id !== featured?.id);
const videos = GUIDES.filter((g) => g.format === 'horizontal' && g.id !== featured?.id);

export default function GuidesPage() {
  // active = { guide, list }: list es la lista que recorren Anterior/Siguiente (o null)
  const [active, setActive] = useState(null);

  const handleClose = useCallback(() => setActive(null), []);
  const playSingle = useCallback((guide) => setActive({ guide, list: null }), []);
  const playShort = useCallback((guide) => setActive({ guide, list: shorts }), []);

  const index = active?.list ? active.list.findIndex((g) => g.id === active.guide.id) : -1;
  const goTo = (i) => setActive({ guide: active.list[i], list: active.list });
  const onPrev = active?.list ? (index > 0 ? () => goTo(index - 1) : null) : undefined;
  const onNext = active?.list ? (index < active.list.length - 1 ? () => goTo(index + 1) : null) : undefined;

  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Primeros Pasos | Guías de inicio para nuevos jugadores"
        description="Aprende a crear e invocar tu Warband, subir a tus alters y comenzar tu aventura en Frostbound. Guías en vídeo para nuevos jugadores de WoW WotLK 3.3.5a."
        canonical="/primeros-pasos"
      />
      <Navbar />

      <main className="guides-main">
        {/* HERO */}
        <section className="guides-hero anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="shop-title" data-testid="guides-heading">
            <Clapperboard size={28} className="shop-title-icon" />
            GUÍAS DE INICIO
          </h1>
          <p className="shop-subtitle">
            Todo lo que necesitas para comenzar tu aventura en{' '}
            <span className="cyan-highlight">Frostbound.</span>
          </p>
        </section>

        {/* FEATURED */}
        <FeaturedGuideCard guide={featured} onPlay={playSingle} />

        {/* GUÍAS RÁPIDAS (9:16) */}
        {shorts.length > 0 && (
          <section className="guides-list anim-fade-up" style={{ animationDelay: '0.4s' }} data-testid="guides-shorts">
            <header className="guides-list-header guides-list-header--stacked">
              <span className="guides-list-header-title">
                <AiFillTikTok size={17} />
                <span>GUÍAS RÁPIDAS · TIKTOK</span>
              </span>
              <span className="guides-list-header-sub">Tutoriales cortos para que avances más rápido.</span>
            </header>
            <ul className="guides-shorts-grid">
              {shorts.map((g) => (
                <li key={g.id}>
                  <ShortCard guide={g} onPlay={playShort} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* GUÍAS EN VÍDEO (16:9) */}
        {videos.length > 0 && (
          <section className="guides-list anim-fade-up" style={{ animationDelay: '0.5s' }}>
            <header className="guides-list-header">
              <BookOpen size={16} />
              <span>MÁS GUÍAS · YOUTUBE</span>
            </header>
            <ul className="guides-list-grid">
              {videos.map((g) => (
                <li key={g.id}>
                  <GuideCard guide={g} onPlay={playSingle} />
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="shop-notice" style={{ marginTop: 28 }}>
          Próximamente más guías — Sugerencias en{' '}
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="cyan-highlight">
            Discord
          </a>
        </p>
      </main>

      <Footer />

      {active && (
        <VideoModal guide={active.guide} onClose={handleClose} onPrev={onPrev} onNext={onNext} />
      )}
    </PageWrapper>
  );
}
